'use client';

import type { INavListItem } from '@/api/types/public';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import NextImage from 'next/image';
import styles from './index.module.scss';

interface ResourceSlotNavProps {
  item: INavListItem;
  firstLevelIndex: number;
  firstLevelTitle: string;
  resourceIndex: number;
}

const DEFAULT_TITLE_COLOR = '#FFFFFF';
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export function getResourceSlotTitleColor(color?: string) {
  return color && HEX_COLOR_PATTERN.test(color) ? color : DEFAULT_TITLE_COLOR;
}

const ResourceSlotNav = ({
  item,
  firstLevelIndex,
  firstLevelTitle,
  resourceIndex,
}: ResourceSlotNavProps) => {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const content = item.content;

  if (!content?.resource_image) return null;

  const title = content.resource_title || '';
  const hasTitle = title.trim() !== '';
  const hasJumpTarget =
    content.Jump_target !== undefined &&
    content.Jump_target !== null &&
    String(content.Jump_target).trim() !== '';
  const jumpTarget = {
    jump_type: content.jump_type ?? 0,
    Jump_target: content.Jump_target ?? '',
    product_group: {
      id: content.product_group?.id,
      path: content.product_group?.path,
    },
    sku: {
      shop_sku: Array.isArray(content.sku) ? content.sku[0] : content.sku?.shop_sku || '',
    },
  };

  const handleBeforeJump = () => {
    setSession('zl_goodsListEnterPageScene', '全局导航');
    setCampaignEntrancePageScene('顶部导航', content.Jump_target);
    trackEvent(
      {
        eventName: 'TopNavigationMkt_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
          tab_name: firstLevelTitle,
          tab_index: firstLevelIndex,
          pits_index: resourceIndex,
          pits_title: title,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const resourceSlotClassName = `relative block shrink-0 self-stretch overflow-hidden ${styles.resourceSlotItem} ${
    hasJumpTarget ? 'group cursor-pointer' : ''
  }`;
  const resourceSlotContent = (
    <>
      <Image
        src={content.resource_image}
        alt={title}
        fill
        sizes="(max-width: 1919px) 415px, 464px"
        className={`object-cover ${
          hasJumpTarget ? 'transition-transform duration-300 group-hover:scale-[1.02]' : ''
        }`}
      />
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-[8px] bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.4)] p-[16px]">
        {hasTitle ? (
          <span
            className="min-w-0 truncate font-bb text-[16px] leading-[19px]"
            style={{ color: getResourceSlotTitleColor(content.resource_title_color) }}
          >
            {title}
          </span>
        ) : null}
        {hasJumpTarget && hasTitle ? (
          <NextImage
            src="/zeelool-next/pc-navigation/arrow.svg"
            alt=""
            width={24}
            height={24}
            className="size-[24px] shrink-0"
            unoptimized
            aria-hidden
          />
        ) : null}
      </div>
    </>
  );

  if (!hasJumpTarget) {
    return <div className={resourceSlotClassName}>{resourceSlotContent}</div>;
  }

  return (
    <MagicJump className={resourceSlotClassName} item={jumpTarget} onBeforeJump={handleBeforeJump}>
      {resourceSlotContent}
    </MagicJump>
  );
};

export default ResourceSlotNav;
